import { Check, X } from '@phosphor-icons/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { REGISTER_UPSELL_VARIANT, REGISTER_UPSELL_VARIANT_BY_CONTEXT, toRegistrationSource } from '../../../const';
import { useIsDesktopViewport } from '../../../hooks/useIsDesktopViewport';
import {
    emailSignUp,
    getAuthError,
    getAuthModalContext,
    getUserLoadingState,
    setAuthFlow,
    toggleAuthModal
} from '../../../store/slices/authSlice';
import CustomButton, { BUTTON_STYLE_TYPE } from '../../common/Button';
import OAuthButtons from './OAuthButtons';
import styles from './styles.module.scss';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// HORO-10509: Sign up is now its own full-height view (Figma "Registration
// screen_general"), replacing the OAuth+email card this used to render
// inside the legacy centered Modal — see AuthModal.js. Reached from Sign
// in's "Don't have an account? Sign up" link, or as the modal's own
// default/fallback flow. All 3 value props show on both breakpoints
// (Figma node 4690:6982). The 4th ("Unlock tarot reading experience") was
// removed — tarot readings aren't part of this offering yet.
//
// HORO-10495/10499/10502: per-entry-point copy variant, keyed by
// REGISTER_UPSELL_VARIANT (selected below from `authModalContext` via
// REGISTER_UPSELL_VARIANT_BY_CONTEXT in const.js). GENERAL is this same,
// already-shipped copy. TAROT/MEMORY/CHAT_HISTORY each have their own
// Figma frame ("Registeration screen_tarot"/"_memory"/"_chat history",
// nodes 4510:25366/25367/25368) — all three reuse the general hero image
// (confirmed via Figma — none of the three references a distinct photo),
// so only copy differs here; no hero-image swap needed for these two.
const VARIANT_COPY = {
    [REGISTER_UPSELL_VARIANT.GENERAL]: {
        titleKey: 'signUpTitle',
        subtitleKey: 'signUpSubtitle',
        benefitKeys: ['signUpBenefit1', 'signUpBenefit2', 'signUpBenefit3']
    },
    [REGISTER_UPSELL_VARIANT.TAROT]: {
        titleKey: 'tarotUpsellTitle',
        subtitleKey: 'tarotUpsellSubtitle',
        benefitKeys: ['tarotUpsellBenefit1', 'tarotUpsellBenefit2', 'tarotUpsellBenefit3', 'tarotUpsellBenefit4']
    },
    [REGISTER_UPSELL_VARIANT.MEMORY]: {
        titleKey: 'memoryUpsellTitle',
        subtitleKey: 'memoryUpsellSubtitle',
        benefitKeys: ['memoryUpsellBenefit1', 'memoryUpsellBenefit2', 'memoryUpsellBenefit3', 'memoryUpsellBenefit4']
    },
    [REGISTER_UPSELL_VARIANT.CHAT_HISTORY]: {
        titleKey: 'chatHistoryUpsellTitle',
        subtitleKey: 'chatHistoryUpsellSubtitle',
        benefitKeys: ['chatHistoryUpsellBenefit1', 'chatHistoryUpsellBenefit2', 'chatHistoryUpsellBenefit3', 'chatHistoryUpsellBenefit4']
    },
    // HORO-10504: locked-advisor entry point — own title/subtitle, no
    // distinct Figma frame for the benefits list so it reuses GENERAL's.
    [REGISTER_UPSELL_VARIANT.ADVISOR_LOCKED]: {
        titleKey: 'advisorLockedUpsellTitle',
        subtitleKey: 'advisorLockedUpsellSubtitle',
        benefitKeys: ['signUpBenefit1', 'signUpBenefit2', 'signUpBenefit3']
    }
};

// HORO-10509: mobile briefly showed the benefits list as its own short
// scroll region — first item full, next one peeking under a blurred
// "there's more" fade (`.benefitsScrollWrap`/`.benefitsScroll`/
// `.benefitsScrollFade`, wired up below) — per direct request, this is
// off for now in favor of just showing all 4 plainly, same as desktop.
// Flip this back to `true` to restore the scroll/peek/blur treatment —
// the classes and the scroll-position tracking below are untouched, just
// unused while this is `false`.
const SHOW_BENEFITS_SCROLL_HINT = false;

const EntryStep = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const isDesktop = useIsDesktopViewport();
    const loading = useSelector(getUserLoadingState);
    const error = useSelector(getAuthError);
    const authModalContext = useSelector(getAuthModalContext);
    const variant = REGISTER_UPSELL_VARIANT_BY_CONTEXT[authModalContext] || REGISTER_UPSELL_VARIANT.GENERAL;
    const { titleKey, subtitleKey, benefitKeys: BENEFIT_KEYS } = VARIANT_COPY[variant];
    const isTarotVariant = variant === REGISTER_UPSELL_VARIANT.TAROT;
    const {
        register,
        handleSubmit,
        trigger,
        formState: { errors, isValid, touchedFields }
    } = useForm({ mode: 'onChange' });

    // react-hook-form's `isValid` defaults to true until the first
    // validation pass — without this, the submit button briefly renders
    // enabled on mount even though the email field is empty.
    useEffect(() => {
        trigger();
    }, [trigger]);

    // HORO-10509: mobile's benefits list is its own scroll region (peeking
    // the next value prop rather than showing all 4 at once) with a blurred
    // bottom edge as a "there's more, scroll" affordance — see
    // `.benefitsScrollFade`. It fades out once the list is scrolled all the
    // way to its last item, since there's nothing further to hint at.
    // `useState` (not a ref) because the class toggle needs a re-render.
    const [isBenefitsScrollEnd, setIsBenefitsScrollEnd] = useState(false);
    const benefitsScrollRef = useRef(null);

    const checkBenefitsScrollEnd = useCallback(() => {
        const node = benefitsScrollRef.current;
        if (!node) {
            return;
        }
        // 1px slack for sub-pixel scroll-height rounding.
        setIsBenefitsScrollEnd(node.scrollHeight - node.scrollTop - node.clientHeight <= 1);
    }, []);

    // Covers the case where all 4 items already fit without scrolling
    // (short copy, tall viewport) — no scroll event will ever fire, so the
    // hint would otherwise stay stuck on.
    useEffect(() => {
        checkBenefitsScrollEnd();
    }, [checkBenefitsScrollEnd]);

    const onSubmit = ({ email }) => {
        if (loading) {
            return;
        }
        dispatch(emailSignUp({ email: email.trim().toLowerCase(), registrationSource: toRegistrationSource(authModalContext) }));
    };

    const onClose = () => dispatch(toggleAuthModal({ trigger: false }));

    const isEmailAlreadyExists = error?.data?.isEmailAlreadyExists;

    // HORO-10509: shared between mobile and desktop — same email field,
    // OAuth row, and footer link in both, just different surrounding
    // chrome (mobile: hero image + header above it; desktop: AuthModal.js
    // renders the shared image/header as its own right column, so this
    // component only ever renders the left-column content there).
    const form = (
        <>
            <OAuthButtons compact dividerFirst={false} desktop={isDesktop} />
            <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
                <div className={styles.inputWrapper}>
                    <input
                        className={styles.fieldInput}
                        type="email"
                        autoComplete="email"
                        placeholder={t('modal.emailPlaceholder')}
                        {...register('email', { required: true, pattern: EMAIL_REGEX })}
                    />
                    {errors.email && touchedFields.email && (
                        <span className={styles.error}>{t('errors.invalidEmail', 'Enter a valid email address')}</span>
                    )}
                </div>
                {/* HORO-10509: this used to also render an inline "Sign in"
                    button next to the error text — dropped since the
                    screen already has its own "Already have an account?
                    Sign in" footer link below, so a second one right here
                    was redundant. */}
                {error && <p className={styles.formError}>{isEmailAlreadyExists ? t('modal.emailAlreadyExists') : error.message}</p>}
                <div className={styles.submitBtnWrap}>
                    <CustomButton
                        text={t('buttons.createAccount')}
                        loading={loading}
                        disable={loading || !isValid}
                        styleType={BUTTON_STYLE_TYPE.PURPLE}
                    />
                </div>
            </form>
            <p className={styles.helperText}>
                {t('modal.alreadyHaveAccount')}{' '}
                <button type="button" className={styles.helperTextAction} onClick={() => dispatch(setAuthFlow('sign-in'))}>
                    {t('modal.signInLink')}
                </button>
            </p>
        </>
    );

    if (isDesktop) {
        // No hero/header here — AuthModal.js's desktop card renders the
        // shared image + logo + X as its own right column. This is just
        // the left column's content.
        return (
            <>
                <h2 className={`${styles.title} ${styles.titleMixedCase} ${styles.signUpTitleDesktop}`}>{t(`modal.${titleKey}`)}</h2>
                <p className={`${styles.subtitle} ${styles.signUpSubtitleDesktop}`}>{t(`modal.${subtitleKey}`)}</p>
                <ul className={`${styles.benefitsList} ${styles.benefitsListDesktop}`}>
                    {BENEFIT_KEYS.map((key) => (
                        <li key={key} className={`${styles.benefitsItem} ${styles.benefitsItemDesktop}`}>
                            <Check size={16} color="#FFFFFF" weight="bold" />
                            {t(`modal.${key}`)}
                        </li>
                    ))}
                </ul>
                {form}
            </>
        );
    }

    return (
        <>
            {/* HORO-10509: hero photo is a normal in-flow section (not a
                fixed background layer) so it scrolls with everything else
                — per the "whole view should move together" fix, real iOS
                Safari broke the earlier fixed-background approach on
                keyboard-open. Per direct request, the brand-wordmark
                header row that used to sit on top of it is gone — the
                close X is its own overlay, pinned level with the head in
                the photo rather than to a header row (see
                styles.module.scss's `.closeIconBtn`). The title (and
                everything below it) no longer overlaps/blurs into the
                photo either — it's a plain sibling starting right at the
                photo's bottom edge, per direct request. */}
            <div className={isTarotVariant ? `${styles.signUpHero} ${styles.signUpHeroTarot}` : styles.signUpHero}>
                <button type="button" className={styles.closeIconBtn} aria-label={t('buttons.close')} onClick={onClose}>
                    <X size={24} color="#FFFFFF" />
                </button>
            </div>
            <div className={styles.signUpHeroTitleWrap}>
                <h2 className={`${styles.title} ${styles.titleMixedCase} ${styles.signUpHeroTitle}`}>{t(`modal.${titleKey}`)}</h2>
            </div>
            <p className={`${styles.subtitle} ${styles.signUpSubtitle}`}>{t(`modal.${subtitleKey}`)}</p>
            {SHOW_BENEFITS_SCROLL_HINT ? (
                /* HORO-10509: its own scroll region rather than flowing with
                   the page — shows the first value prop clearly with the next
                   one peeking (and lightly blurred) at the bottom to invite
                   scrolling. `.benefitsScrollFade` is a separate sibling
                   (not a child of the scrollable node) so it stays pinned to
                   the bottom edge instead of scrolling away with the list;
                   it's hidden once scroll reaches the last item. */
                <div className={styles.benefitsScrollWrap}>
                    <ul
                        className={`${styles.benefitsList} ${styles.benefitsScroll}`}
                        ref={benefitsScrollRef}
                        onScroll={checkBenefitsScrollEnd}
                    >
                        {BENEFIT_KEYS.map((key) => (
                            <li key={key} className={styles.benefitsItem}>
                                <Check size={16} color="#FFFFFF" weight="bold" />
                                {t(`modal.${key}`)}
                            </li>
                        ))}
                    </ul>
                    <div
                        className={`${styles.benefitsScrollFade} ${isBenefitsScrollEnd ? styles.benefitsScrollFadeHidden : ''}`}
                        aria-hidden="true"
                    />
                </div>
            ) : (
                // Plain list, all 4 shown at once — same treatment as
                // desktop's, no scroll/blur.
                <ul className={styles.benefitsList}>
                    {BENEFIT_KEYS.map((key) => (
                        <li key={key} className={styles.benefitsItem}>
                            <Check size={16} color="#FFFFFF" weight="bold" />
                            {t(`modal.${key}`)}
                        </li>
                    ))}
                </ul>
            )}
            {form}
        </>
    );
};

export default EntryStep;
